import { assets } from "@core/assets";
import { cn } from "@core/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from "react";

const checkboxStyles = cva(
  [
    "relative",
    "cursor-pointer",
    "flex",
    "items-center",
    "justify-center",
    "transition-all",
    "duration-200",
    "ease-in-out",
    "w-4",
    "h-4",
    "rounded-sm",
  ],
  {
    variants: {
      variant: {
        primary: "bg-white border-[1px] border-neutral-200 hover:bg-neutral-50",
      },
      state: {
        checked:
          "bg-primary-800 text-white border-primary-800 hover:bg-primary-900 hover:text-white",
        indeterminate:
          "bg-primary-800 text-white border-primary-800 hover:bg-primary-900",
        error: "bg-accent-red-50 border-accent-red-800 hover:bg-accent-red-50",
        default: "",
      },
      proportions: {
        lg: "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

type CheckboxProps = ComponentProps<"input"> &
  VariantProps<typeof checkboxStyles> & {
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onClickAction?: () => void;
    checked?: boolean;
    indeterminate?: boolean;
  };

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      variant,
      className,
      state,
      proportions,
      onChange,
      onClickAction,
      disabled,
      checked,
      indeterminate = false, // NEW
      ...props
    },
    ref,
  ) => {
    const [isChecked, setIsChecked] = useState(
      checked ?? props.defaultChecked ?? false,
    );

    const CheckIcon = assets["check"];
    const inputRef = useRef<HTMLInputElement>(null);

    // keep internal state in sync if controlled
    useEffect(() => {
      if (checked !== undefined) {
        setIsChecked(checked);
      }
    }, [checked]);

    // set native indeterminate prop on the input
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);

    const handleWrapperClick = () => {
      if (disabled) return;
      inputRef.current?.click();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      const newCheckedState = event.target.checked;

      if (checked === undefined) {
        setIsChecked(newCheckedState);
      }

      onChange?.(event);
      onClickAction?.();
    };

    const isCheckedFinal = checked !== undefined ? checked : isChecked;

    return (
      <div className="inline-flex items-center">
        <div
          onClick={handleWrapperClick}
          className={cn(
            checkboxStyles({
              variant,
              state: indeterminate
                ? "indeterminate"
                : isCheckedFinal
                  ? "checked"
                  : state === "error"
                    ? "error"
                    : "default",
              proportions,
            }),
            className,
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          )}
        >
          <input
            type="checkbox"
            ref={(element) => {
              if (typeof ref === "function") {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
              inputRef.current = element;
            }}
            checked={isCheckedFinal}
            onChange={handleChange}
            className="opacity-0 absolute h-0 w-0"
            disabled={disabled}
            {...props}
          />
          {/* Icon / indicator */}
          {indeterminate ? (
            <span className="block w-2.5 h-0.5 bg-white rounded-sm" />
          ) : (
            isCheckedFinal && <CheckIcon className="text-xs" strokeWidth={2} />
          )}
        </div>
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
